import * as React from 'react';
import Button from '../../../core/components/Button.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    faArrowUpRightFromSquare,
    faChevronDown,
    faChevronUp,
    faEdit,
    faList,
    faThumbsDown,
    faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext.tsx';
import { useListOfServicesAssistanceContext } from '../pages/Assistance.tsx';
import { LIST_OF_SERVICE_ASSISTANCE_MODES } from './ListOfServicesAssistanceHeader.tsx';
import Switch from '../../../core/components/Switch.tsx';

const NOT_RELEVANT_REASON_OTHER = 'OTHER';
const NOT_RELEVANT_REASONS = [
    'NOT_AN_LV_POSITION',
    'NO_PRODUCT',
    'MISSING_TEXT',
    'TOO_MUCH_TEXT',
    NOT_RELEVANT_REASON_OTHER,
];

const ThumbsUpIcon = withIcon(faThumbsUp);
const ThumbsDownIcon = withIcon(faThumbsDown);
const ListIcon = withIcon(faList);
const EditIcon = withIcon(faEdit);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);

interface NotRelevantBannerProps {
    reason?: string;
    onReasonChange?: (reason: string) => void;
}

const NotRelevantBanner = ({ reason }: NotRelevantBannerProps) => {
    const { t } = useTranslation('assistance');

    return (
        <div className="flex flex-col gap-3 bg-surface-strong px-5 py-4 rounded">
            <div className="text-sm text-content-medium">{t('listOfServices.positionCard.notRelevantTitle')}</div>

            <div className="flex gap-2 items-center text-content-strong">
                <div className="text-sm">
                    {(reason && t(`listOfServices.positionCard.reasons.USER`, { username: reason })) ||
                        t(`listOfServices.positionCard.reasons.SYSTEM`)}
                </div>
            </div>
        </div>
    );
};

const FeedbackNeededBanner = ({ isRelevant }) => {
    const { t } = useTranslation('assistance');

    return (
        <div className="text-content-warning-strong bg-surface-warning-weak px-5 py-4 rounded text-sm">
            <Trans
                t={t}
                i18nKey={
                    isRelevant
                        ? 'listOfServices.positionCard.feedbackNeededRelevant'
                        : 'listOfServices.positionCard.feedbackNeededNotRelevant'
                }
                components={{ b: <strong /> }}
            />
        </div>
    );
};

interface PositionAssistanceCardProps {
    className?: string;
    number: string;
    description: React.ReactNode;
    isRelevant?: boolean;
    isFeedbackNeeded?: boolean;
    onRelevantChange?: (isRelevant: boolean) => void;
    reason?: string;
    pageIndex?: number;
    articlesCount?: number;
    isIncluded?: boolean;
    onIncludedChange?: (isIncluded: boolean) => void;
    variant?: 'position' | 'pretext';
}

const PositionAssistanceCard = ({
    className,
    number,
    description,
    isRelevant,
    isFeedbackNeeded,
    onRelevantChange,
    isIncluded,
    onIncludedChange,
    reason,
    pageIndex,
    variant = 'position',
}: PositionAssistanceCardProps) => {
    const { readOnly, loading, record } = useAssistanceContext();

    const { mode } = useListOfServicesAssistanceContext();

    const { t } = useTranslation('assistance');

    const handleRelevantChange = (isRelevant: boolean) => onRelevantChange?.(isRelevant);

    return (
        <div
            className={classnames(
                'flex flex-col rounded-lg border border-line-weak border-solid',
                variant === 'position' && 'bg-surface-weak shadow-md',
                className
            )}
            data-scroll-anchor={isRelevant ? 'position' : undefined}
        >
            <div className="p-6 flex flex-col gap-2 text-sm overflow-hidden text-content-strong whitespace-pre-line">
                {description}
            </div>

            <div className="border-b border-line-weak border-solid" />

            <div className="flex flex-col gap-4 py-4 px-6">
                <div className="flex gap-2 justify-between items-center">
                    <div className="flex gap-2">
                        <Button
                            disabled={readOnly || loading}
                            variant="ghost"
                            className={classnames(
                                'flex gap-2 items-center hover:bg-surface-success-weak hover:text-content-success-strong',
                                isRelevant === true && 'bg-surface-success-weak text-content-success-strong',
                                isRelevant === true &&
                                    isFeedbackNeeded &&
                                    'bg-surface-warning-weak text-content-warning-strong'
                            )}
                            onClick={() => {
                                if (isRelevant === false || isFeedbackNeeded) {
                                    handleRelevantChange(true);
                                }
                            }}
                        >
                            <ThumbsUpIcon /> {t('listOfServices.positionCard.relevant')}
                        </Button>

                        {mode === LIST_OF_SERVICE_ASSISTANCE_MODES.EDITOR && (
                            <Button
                                disabled={readOnly || loading}
                                variant="ghost"
                                className={classnames(
                                    'flex gap-2 items-center hover:bg-surface-danger-weak hover:text-content-danger-strong',
                                    isRelevant === false && 'bg-surface-danger-weak text-content-danger-strong',
                                    isRelevant === false &&
                                        isFeedbackNeeded &&
                                        'bg-surface-warning-weak text-content-warning-strong'
                                )}
                                onClick={() => {
                                    if (isRelevant === true || isFeedbackNeeded) {
                                        handleRelevantChange(false);
                                    }
                                }}
                            >
                                <ThumbsDownIcon /> {t('listOfServices.positionCard.notRelevant')}
                            </Button>
                        )}
                    </div>

                    {mode === LIST_OF_SERVICE_ASSISTANCE_MODES.EDITOR && isRelevant === true && (
                        <div className="flex gap-3 items-center">
                            <label className="text-content-strong font-medium text-sm">
                                {t('listOfServices.positionCard.include')}
                            </label>
                            <Switch value={isIncluded} onValueChange={onIncludedChange} />
                        </div>
                    )}
                </div>

                {isRelevant === false && !isFeedbackNeeded && <NotRelevantBanner reason={reason} />}

                {isFeedbackNeeded && <FeedbackNeededBanner isRelevant={isRelevant} />}

                {isRelevant === true && isIncluded === false && (
                    <div className="text-sm text-content-strong bg-surface-strong rounded px-5 py-4">
                        {t('listOfServices.positionCard.notIncluded')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PositionAssistanceCard;
